// @givmo/org-console — a kept address's pure rules (Org Machinery OM-64 and OM-59, Design 4C §3 and
// §8).
//
// The physical address is a FACT the organization keeps on seam A (`kind: "address"`): one structured
// value, judged whole by the server. This file holds what the page needs to draw it and to write it,
// and nothing that judges it — the server refuses a P.O. box, a private mailbox, an unknown state or a
// malformed ZIP code, and the card shows the line it serves (the stale line's posture).
//
//   * the lines a kept address reads as — the county with its label ("County: King", D-CPO-69 (1)),
//     as a person reads the card's other lines;
//   * the lines an outside record's address reads as — its in-care-of name first, the record's "%"
//     shown as "c/o" (D-CPO-69 (1); a display convention only, the record is never rewritten);
//   * a served label inside a sentence ("Mailing address" → "mailing address");
//   * the value a write sends: each part trimmed with inner runs of white space collapsed, an empty
//     optional part absent, the country filled in — the server's own normalization, so "no change"
//     compares like for like and nobody's casing is touched;
//   * whether a kept address is the same as a record's mailing address (D-CPO-61: line 1, line 2,
//     city, state and ZIP, without regard to case), and the record's address as a kept value, for the
//     "Same as mailing address" shortcut, which copies once and never follows a later change;
//   * whether a record's address names a box — the server's own rule, mirrored ONLY to decide whether
//     the shortcut is offered at all: a P.O. box or a private mailbox can never be a physical address,
//     so the card opens the fields instead of offering a copy the server would refuse. The server
//     still judges every write.

import type { OrgAddress, OrgRecordAddress } from './wire-types';

/** The address form's draft: every part as typed, `''` for an empty one. */
export interface OrgAddressDraft {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  county: string;
}

export const EMPTY_ADDRESS_DRAFT: OrgAddressDraft = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  county: '',
};

/** Trimmed, with inner runs of white space collapsed — never re-cased. */
function collapse(text: string | null | undefined): string {
  return (text ?? '').split(/\s+/).filter(Boolean).join(' ');
}

/** The form's draft for an address, or the empty draft for none. */
export function addressDraftFrom(address: OrgAddress | null): OrgAddressDraft {
  if (address === null) return EMPTY_ADDRESS_DRAFT;
  return {
    line1: address.line1,
    line2: address.line2 ?? '',
    city: address.city,
    state: address.state,
    postal_code: address.postal_code,
    county: address.county ?? '',
  };
}

/** The value a write sends for a draft — the server's own shape, normalized as it normalizes. */
export function addressValueFrom(draft: OrgAddressDraft): Record<string, string | null> {
  const optional = (text: string) => {
    const collapsed = collapse(text);
    return collapsed.length === 0 ? null : collapsed;
  };
  return {
    line1: collapse(draft.line1),
    line2: optional(draft.line2),
    city: collapse(draft.city),
    state: collapse(draft.state).toUpperCase(),
    postal_code: collapse(draft.postal_code),
    county: optional(draft.county),
    country: 'US',
  };
}

/** Whether a draft says anything other than what the record holds (the form's dirty state). */
export function addressDraftDiffers(draft: OrgAddressDraft, address: OrgAddress | null): boolean {
  const asked = addressValueFrom(draft);
  const held = addressValueFrom(addressDraftFrom(address));
  return (Object.keys(asked) as (keyof typeof asked)[]).some((part) => asked[part] !== held[part]);
}

/**
 * Whether the record already holds what was asked — compared part by part on the normalized values,
 * never by object identity, so a failed save is told apart from a record another person moved to the
 * very same address.
 */
export function addressHolds(address: OrgAddress | null, asked: unknown): boolean {
  if (address === null || typeof asked !== 'object' || asked === null) return false;
  const want = asked as Record<string, unknown>;
  const held = addressValueFrom(addressDraftFrom(address));
  return (Object.keys(held) as (keyof typeof held)[]).every(
    (part) => (held[part] ?? null) === ((want[part] as string | null | undefined) ?? null),
  );
}

/** The county's line on a card: its label first, so a county typed as "King" never reads as a name. */
export function countyLine(county: string): string {
  return `County: ${county}`;
}

/** The lines an address reads as: the street lines, "City, ST ZIP", then the county with its label. */
export function addressLines(address: OrgAddress): string[] {
  return [
    address.line1,
    ...(address.line2 === null ? [] : [address.line2]),
    `${address.city}, ${address.state} ${address.postal_code}`,
    ...(address.county === null ? [] : [countyLine(address.county)]),
  ];
}

/**
 * The in-care-of line as people write it: the record's own marker, "%", reads "c/o" (a "%" copied
 * into a form is a typo); a line that already says how it is cared for keeps its words. Display only.
 */
export function inCareOfLine(inCareOf: string): string {
  if (inCareOf.startsWith('%')) return `c/o ${inCareOf.slice(1).trim()}`;
  if (/^(c\/o|care of|attn)\b/i.test(inCareOf)) return inCareOf;
  return `c/o ${inCareOf}`;
}

/** An outside record's address as it holds it: the in-care-of name first, then the street lines. */
export function recordAddressLines(address: OrgRecordAddress): string[] {
  const place = [address.city, [address.state, address.postal_code].filter(Boolean).join(' ')]
    .filter((part) => part !== null && part.length > 0)
    .join(', ');
  return [
    address.in_care_of === null ? null : inCareOfLine(address.in_care_of),
    address.line1,
    address.line2,
    place,
  ].filter((line): line is string => line !== null && line.length > 0);
}

/**
 * A served label inside a sentence: its first letter lower-cased ("Physical address" → "physical
 * address"), unless it starts an acronym ("EIN" stays "EIN").
 */
export function labelMidSentence(label: string): string {
  if (label.length < 2 || label[1] !== label[1]?.toLowerCase()) return label;
  return `${label[0]?.toLowerCase() ?? ''}${label.slice(1)}`;
}

/**
 * A P.O. box or drawer, and a mail-receiving agency's box — the server's own patterns
 * (givmo-backend-python `app/domain/org_settings.py` `_PO_BOX`, `_PRIVATE_MAILBOX`). A rural-route
 * box names no post office and is not one.
 */
const PO_BOX =
  /\b(?:p\.?\s?o\.?\s?(?:box|drawer)(?![a-z])|post\s+office\s+(?:box|drawer)(?![a-z])|p\.?\s?o\.?\s?b(?![a-z]))/i;
const PRIVATE_MAILBOX = /\b(?:pmb|private\s+mail\s?box)(?![a-z])/i;

/** Which box a record's street lines name, if any: the shortcut is then never offered. */
export function recordAddressBox(address: OrgRecordAddress): 'po_box' | 'private_mailbox' | null {
  const lines = [address.line1, address.line2].filter((line): line is string => line !== null);
  if (lines.some((line) => PO_BOX.test(line))) return 'po_box';
  if (lines.some((line) => PRIVATE_MAILBOX.test(line))) return 'private_mailbox';
  return null;
}

const sameText = (a: string | null, b: string | null) =>
  collapse(a).toLowerCase() === collapse(b).toLowerCase();

/** Whether a kept address is the record's mailing address: line 1, line 2, city, state and ZIP, any case. */
export function addressMatchesRecord(address: OrgAddress, record: OrgRecordAddress): boolean {
  return (
    sameText(address.line1, record.line1) &&
    sameText(address.line2, record.line2) &&
    sameText(address.city, record.city) &&
    sameText(address.state, record.state) &&
    sameText(address.postal_code, record.postal_code)
  );
}

/**
 * The record's address as a kept value — what the shortcut writes. `null` when the record lacks a part
 * a kept address needs, so the shortcut is not offered at all. The in-care-of name is a mailing line,
 * never a place, so it is not copied; nor is a county, which the record does not hold.
 */
export function addressValueFromRecord(
  record: OrgRecordAddress,
): Record<string, string | null> | null {
  if (record.line1 === null || record.city === null || record.state === null) return null;
  if (record.postal_code === null) return null;
  return addressValueFrom({
    line1: record.line1,
    line2: record.line2 ?? '',
    city: record.city,
    state: record.state,
    postal_code: record.postal_code,
    county: '',
  });
}
